import React, { useEffect, useMemo, useRef, useState, createRef } from "react";
import { Formik } from "formik";
import * as Yup from "yup";
import { useParams } from "react-router";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import CustomerSetup from "src/components/project/settings/CustomerSetup";
import KeyDetails from "src/components/project/settings/KeyDetails";
import MovewareDetails from "src/components/project/settings/MovewareDetails";
import ContactDetails from "src/components/project/settings/ContactDetails";
import ReportSettings from "src/components/project/settings/ReportSettings";
import useMutation from "src/hooks/useMutation";
import {
  UPDATE_PROJECT_MUTATION,
  UPDATE_PROJECT_SETTINGS_MUTATION,
} from "src/constants/graphql/mutations";
import useNotification from "src/hooks/notification";
import { getProjectFieldSettingsVariables, getProjectVariables } from "src/utils/profile/settings";
import { useProject } from "../ProjectProvider";
import useAuth from "src/hooks/useAuth";
import BillingSettings from "./BillingSettings";

const styles = {
  saveButton: {
    ml: 1,
  },
  stickyFooter: {
    position: "sticky",
    bottom: 0,
    bgcolor: "background.paper",
    borderTop: 1,
    borderColor: "divider",
    p: 2,
    zIndex: 1,
  },
};

const emailListValidator = value => {
  if (!value) return true;

  const emails = value
    .split(/[,;]+/)
    .map(e => e.trim())
    .filter(e => e.length > 0);
  const emailRegex = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@(([^<>()[\]\.,;:\s@"]+\.)+[^<>()[\]\.,;:\s@"]{2,})$/i;

  for (let email of emails) {
    if (!emailRegex.test(email)) {
      return false;
    }
  }
  return true;
};

export const formikRef = createRef();

const buildInitialValues = (project, client) => ({
  ...project,
  active: !project.disabled,
  clientName: client?.name,
  billingRates: project.billingRates || {},
  laborRates: project.laborRates || {},
  materialRates: project.materialRates || {},
  openInspectRates: project.openInspectRates || {},
  equipmentRates: project.equipmentRates || {},
  billingTypes: project.billingTypes || {},
  billingNotes: project.billingNotes || "",
});

export default function Settings({ onClose }) {
  const { projectId } = useParams();
  const { notifySuccess } = useNotification();
  const [updateProject] = useMutation(UPDATE_PROJECT_MUTATION);
  const [updateProjectSettings] = useMutation(UPDATE_PROJECT_SETTINGS_MUTATION);
  const { project, client } = useProject();
  const { user } = useAuth();
  const { settings } = user.company;
  const readOnly = user.role === "billing_user";
  const serverInitialValues = useMemo(() => buildInitialValues(project, client), [project, client]);
  const [initialValues, setInitialValues] = useState(serverInitialValues);
  const saveButtonRef = useRef();

  useEffect(() => {
    const isDifferentProject = initialValues.id !== serverInitialValues.id;
    if (isDifferentProject || !formikRef.current?.dirty) {
      setInitialValues(serverInitialValues);
    }
  }, [initialValues.id, serverInitialValues]);

  const validationSchema = Yup.object().shape(
    Object.fromEntries(
      [
        ["customerEmailSendList", true],
        ["osDEmailSendList", project.osDEmailSendList],
        ["billingEmailSendList", project.billingEmailSendList],
        ["approvedEmailSendList", project.approvedEmailSendList],
        ["outboundOnlyEmailSendList", project.outboundOnlyEmailSendList],
        ["deliveryOnlyEmailSendList", project.deliveryOnlyEmailSendList],
        ["automatedReportsEmailSendList", project.automatedReportsEmailSendList],
      ]
        .filter(([field, enabled]) => enabled)
        .map(([field]) => [
          field,
          Yup.string()
            .nullable()
            .test(
              "valid-email-list",
              "Please enter one or more valid email addresses, separated by a comma.",
              emailListValidator
            ),
        ])
    )
  );

  const handleSubmit = async (values, { resetForm }) => {
    if (readOnly) return;

    if (initialValues.billingType !== values.billingType) {
      await updateProjectSettings({
        variables: {
          projectId,
          fieldSettings: getProjectFieldSettingsVariables(
            values.billingType,
            project.fieldSettings
          ),
          receivingSettings: getProjectFieldSettingsVariables(
            values.billingType,
            project.receivingSettings
          ),
        },
      });
    }

    const variables = getProjectVariables({ ...values, id: projectId });
    return updateProject({
      variables,
      onSuccess() {
        setInitialValues(values);
        resetForm({ values });
        notifySuccess(`${settings.filter_field2} has been updated`);
      },
    });
  };

  const triggerSave = () => {
    if (saveButtonRef.current) {
      saveButtonRef.current.click();
    }
  };

  return (
    <Formik
      innerRef={formikRef}
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ handleSubmit, isSubmitting, values }) => (
        <>
          <form noValidate onSubmit={handleSubmit}>
            <Box>
              <KeyDetails project={project} />
            </Box>
            <MovewareDetails projectId={project.id} />
            <Box mt={2}>
              <CustomerSetup project={project} onToggleProjectInfoModal={triggerSave} />
            </Box>
            {(values.storageEnabled || settings?.inventory_tracking) && (
              <Box mt={2}>
                <ContactDetails />
              </Box>
            )}
            <Box mt={2}>
              <ReportSettings project={project} storageEnabled={values.storageEnabled} />
            </Box>
            {values.storageEnabled && values.billing && (
              <Box mt={2}>
                <BillingSettings project={project} />
              </Box>
            )}
            <Box mt={2} display="flex" justifyContent="flex-end" sx={styles.stickyFooter}>
              <Button variant="outlined" color="primary" onClick={onClose}>
                Close
              </Button>
              {!readOnly && (
                <Button
                  ref={saveButtonRef}
                  type="submit"
                  variant="contained"
                  color="primary"
                  sx={styles.saveButton}
                  disabled={isSubmitting}
                  startIcon={isSubmitting && <CircularProgress size={22} />}
                >
                  Save
                </Button>
              )}
            </Box>
          </form>
        </>
      )}
    </Formik>
  );
}
